import React, { useEffect, useState } from 'react';
import { CheckCircle2, Save } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';
import { DetailField } from '../course/DetailField';
import { formatEuro } from '../../../utils/currency';
import { withVat } from '../../../utils/trainingBilling';
import { ProformaDocument } from '../../../types/trainingBilling';
interface ProformaSectionProps {
  proforma: ProformaDocument;
  productLabel: string;
  productCode: string;
  amount?: number;
  onSave: (proforma: ProformaDocument) => void;
}
export function ProformaSection({
  proforma,
  productLabel,
  productCode,
  amount,
  onSave
}: ProformaSectionProps) {
  const [draft, setDraft] = useState(proforma);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  useEffect(() => setDraft(proforma), [proforma]);
  const update = (changes: Partial<ProformaDocument>) => {
    setDraft(d => ({
      ...d,
      ...changes
    }));
    setSaved(false);
    setErrors(e => {
      const next = {
        ...e
      };
      Object.keys(changes).forEach(k => delete next[k]);
      return next;
    });
  };
  const save = () => {
    const next: Record<string, string> = {};
    if (!draft.issueDate) next.issueDate = 'Inserisci la data di emissione';
    if (!draft.number?.trim()) next.number = 'Inserisci il numero di proforma';
    if (draft.issueDate && draft.dueDate && draft.dueDate < draft.issueDate) next.dueDate = 'La scadenza non può precedere l’emissione';
    setErrors(next);
    if (Object.keys(next).length) return;
    onSave({
      ...draft,
      number: draft.number?.trim()
    });
    setSaved(true);
  };
  return <Card title="Proforma" noHoverShadow data-id="element-1059" data-mp="sx2l2rb2t58n">
      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4 mb-6" data-id="element-1060" data-mp="suq4ahh210x6">
        <DetailField label="Numero progressivo" data-id="element-1061" data-mp="2pqxa160lzxn7">
          <span className="font-mono" data-id="element-1062" data-mp="2unnrnxs2b1aq">{draft.progressive}</span>
        </DetailField>
        <DetailField label="Prodotto e codice prodotto" data-id="element-1063" data-mp="2mn7ztig9gsnp">
          {productLabel} · <span className="font-mono" data-id="element-1064" data-mp="2mpkglsaa8wz6">{productCode}</span>
        </DetailField>
        <DetailField label="Importo senza IVA" data-id="element-1065" data-mp="2inh2evw8bea7">
          {amount !== undefined && formatEuro(amount)}
        </DetailField>
        <DetailField label="Importo con IVA (22%)" data-id="element-1066" data-mp="2wjb16m3agn9l">
          {amount !== undefined && formatEuro(withVat(amount))}
        </DetailField>
      </dl>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-1067" data-mp="35laizgm4nvdl">
        <Input label="Data di emissione" type="date" value={draft.issueDate || ''} onChange={e => update({
        issueDate: e.target.value || undefined
      })} description="Compilata in automatico, modificabile" error={errors.issueDate} required data-id="element-1068" data-mp="31kql2atqcnqe" />
        <Input label="Data di scadenza" type="date" value={draft.dueDate || ''} onChange={e => update({
        dueDate: e.target.value || undefined
      })} error={errors.dueDate} data-id="element-1069" data-mp="3p9teefxq850q" />
        <Input label="Numero di proforma" value={draft.number || ''} onChange={e => update({
        number: e.target.value
      })} placeholder="es. PRO 120/2026" error={errors.number} required data-id="element-1070" data-mp="7syvgn9n9hfl" />
      </div>
      <div className="flex items-center justify-end gap-3 mt-6" data-id="element-1071" data-mp="2s1euf4a8vg7">
        {saved && <span role="status" className="flex items-center gap-1.5 text-sm text-green-700" data-id="element-1072" data-mp="3k9av87rsyqn8">
            <CheckCircle2 className="h-4 w-4" data-id="element-1073" data-mp="3ozr9tv7d0ib6" /> Proforma salvata
          </span>}
        <Button startContent={<Save className="h-4 w-4" data-id="element-1075" data-mp="3b8mdp337a85l" />} onClick={save} data-id="element-1074" data-mp="3p1lxhha0moiy">
          Salva proforma
        </Button>
      </div>
    </Card>;
}